/**
 * localStorage-backed note store.
 *
 * Privacy: notes are encoded deposit secrets. They never leave the
 * browser — no analytics, no server sync. Browser cache wipes them.
 *
 * The Notes tab + the Deposit flow both write here; the Withdraw flow
 * reads to offer a "use saved note" picker.
 */

const STORAGE_KEY = "tonado.notes.v1";

export type StoredNote = {
  raw: string;
  /** Unix ms timestamp. */
  savedAt: number;
};

export function loadStoredNotes(): StoredNote[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as StoredNote[];
  } catch {
    return [];
  }
}

function persist(rows: StoredNote[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // Quota exceeded, private mode, etc. — best-effort, fail silently.
  }
}

/**
 * Append a freshly-generated note. Dedupes by `raw` string so re-saves
 * don't multiply. Returns the new list (caller may use for state update).
 */
export function addStoredNote(raw: string): StoredNote[] {
  const trimmed = raw.trim();
  const prior = loadStoredNotes().filter((n) => n.raw !== trimmed);
  const next = [{ raw: trimmed, savedAt: Date.now() }, ...prior];
  persist(next);
  return next;
}

export function removeStoredNote(raw: string): StoredNote[] {
  const next = loadStoredNotes().filter((n) => n.raw !== raw);
  persist(next);
  return next;
}
